import { useEffect, useState } from 'react';
import { Refresh } from '@mui/icons-material';
import Loading from '../../../../common/loading';
import Table from '../../../../common/table';
import l from '../../../../../lang';
import './styles.scss';
import MessageError from '../../../../common/message-error';
import { usePaymentLink } from '../../../../../store/payment-link';
import Moment from 'react-moment';
import { useHistory } from 'react-router-dom';
import CopyToClipboard from 'react-copy-to-clipboard';
import Tooltip from '@mui/material/Tooltip';
import { ReactComponent as CopyIcon } from '../../../../../assets/icons/copy.svg';
import { ReactComponent as EmptyIcon } from '../../../../../assets/icons/empty-payment-links.svg';
import ButtonLink from '../../../../common/button-link';
import { Routes } from '../../../../../config/routes';
import { ReactComponent as AllCountriesIcon } from '../../../../../assets/icons/all-countries.svg';
import { ReactComponent as ShareIcon } from '../../../../../assets/icons/share.svg';
import { ReactComponent as ShareDisableIcon } from '../../../../../assets/icons/share-disable.svg';
import { Button } from '../../../../common/button';
import { RecurringPaymentLink } from '../../../../../store/payment-link/interfaces';
import PaymentLinkDialog from '../../payment-link-dialog';
import Switch from '../../../../common/switch';
import { getCountryFlagUrl } from '../../../../../utils';
import { useSelector } from 'react-redux';
import { canViewPaymentLinksTables, canCreatePaymentLinks, canChangePaymentLinkStatus } from '../../../../../store/payment-link/roles';
import { segmentPaymentLinkLinkListCopyLink } from '../../../../../utils/analytics';

const CreatedRecurringPaymentLink = () => {
  const history = useHistory();
  const { auth } = useSelector(state => state);
  const canViewTable = canViewPaymentLinksTables(auth?.roles);
  const canCreate = canCreatePaymentLinks(auth?.roles);
  const canChangeStatus = canChangePaymentLinkStatus(auth?.roles);
  const [share, setShare] = useState<RecurringPaymentLink | null>(null);
  const [copiedLinkToken, setCopiedLinkToken] = useState<string | null>(null);

  const { createdRecurringPaymentLinks, getCreatedRecurringPaymentLinks, changeStatusRecurringPaymentLink } =
    usePaymentLink();
  const { loading, error, allPaymentLinks } = createdRecurringPaymentLinks;

  useEffect(() => {
    if (canViewTable) {
      getCreatedRecurringPaymentLinks();
    }
  }, [canViewTable]);

  const dataExist = allPaymentLinks && allPaymentLinks?.data?.length > 0;

  return (
    <div className="created_recurring_payment_link">
      {canViewTable && loading && (
        <div className="created_recurring_payment_link__loading">
          <Loading />
        </div>
      )}
      {canViewTable && error && (
        <div className="created_recurring_payment_link__error">
          <MessageError iconClose={<Refresh />} onClose={() => getCreatedRecurringPaymentLinks()} />
        </div>
      )}
      {canViewTable && !loading && !error && (
        <div className="created_recurring_payment_link__table">
          <Table
            dense
            cellAlign="left"
            headers={[
              l('createdPaymentLink.creationDate'),
              l('createdPaymentLink.link'),
              l('createdPaymentLink.country'),
              l('createdPaymentLink.description'),
              l('createdPaymentLink.amount'),
              l('createdPaymentLink.status'),
              l('createdPaymentLink.action'),
            ]}
            emptyBody={
              <div className="created_recurring_payment_link__no_data">
                <EmptyIcon />
                <div className="created_recurring_payment_link__no_data__title">
                  {l('createdPaymentLink.noRecurringPaymentLinks')}
                </div>
                <div>
                  <ButtonLink
                    text={l('createdPaymentLink.noPaymentLinkDesc.1')}
                    onClick={() => history.push(Routes.CREATE_RECURRING_PAYMENT_LINK)}
                    disabled={!canCreate}
                  />
                  {l('createdPaymentLink.noPaymentLinkDesc.2')}
                </div>
              </div>
            }
            body={
              (dataExist &&
                allPaymentLinks?.data?.map(pl => [
                  <span>
                    {pl.createdAt ? <Moment format="DD/MM/YYYY">{pl.createdAt}</Moment> : '-'}
                  </span>,
                  <div className="created_recurring_payment_link__copy-cell">
                    {pl.recurringPaymentLinkUrl ? (
                      <CopyToClipboard
                        text={pl.recurringPaymentLinkUrl}
                        onCopy={() => {
                          segmentPaymentLinkLinkListCopyLink({
                            link: pl.recurringPaymentLinkUrl,
                          });
                          setCopiedLinkToken(pl.recurringLinkToken);
                        }}
                      >
                        <Tooltip
                          onMouseEnter={() => setCopiedLinkToken(null)}
                          title={l(
                            copiedLinkToken === pl.recurringLinkToken
                              ? 'createdPaymentLink.copiedLink'
                              : 'createdPaymentLink.copyLink'
                          )}
                          arrow
                        >
                          <button type="button" className="created_recurring_payment_link__copy-link">
                            <CopyIcon className="created_recurring_payment_link__copy-link-icon" aria-hidden />
                            <span>{l('createdPaymentLink.copyLink')}</span>
                          </button>
                        </Tooltip>
                      </CopyToClipboard>
                    ) : (
                      '-'
                    )}
                  </div>,
                  <span>
                    {pl.country ? (
                      <span className="created_recurring_payment_link__country">
                        <img
                          alt=""
                          className="created_recurring_payment_link__country_flag"
                          src={getCountryFlagUrl(pl.country)}
                        />
                        <span>&nbsp;{pl.country}</span>
                      </span>
                    ) : (
                      <span className="created_recurring_payment_link__country">
                        <AllCountriesIcon />
                        <span>&nbsp;{l('common.all.m')}</span>
                      </span>
                    )}
                  </span>,
                  <span>{pl.description || '-'}</span>,
                  <span>{pl.currency && pl.amount ? pl.currency + ' ' + pl.amount : '-'}</span>,
                  <span className="created_recurring_payment_link__status">
                    <Switch
                      checked={pl.enabled}
                      disabled={!canChangeStatus}
                      onChange={() => changeStatusRecurringPaymentLink(pl.recurringLinkToken, !pl.enabled)}
                    />
                    <span className="created_recurring_payment_link__status__text">
                      {pl.enabled ? l('createdPaymentLink.status.active') : l('createdPaymentLink.status.inactive')}
                    </span>
                  </span>,
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={pl.enabled ? <ShareIcon /> : <ShareDisableIcon />}
                      onClick={() => setShare(pl)}
                      disabled={!pl.enabled}
                    >
                      {l('createdPaymentLink.share')}
                    </Button>
                  </span>,
                ])) ||
              null
            }
            rowsPerPageOptions={[10, 25, 50]}
            rowsLength={allPaymentLinks?.totalElements}
            rowsPerPage={allPaymentLinks?.size}
            page={allPaymentLinks?.page}
            handleChangePage={(event, newPage) => getCreatedRecurringPaymentLinks(newPage, allPaymentLinks?.size)}
            handleChangeRowsPerPage={event => getCreatedRecurringPaymentLinks(0, event.target.value)}
          />
          {share && (
            <PaymentLinkDialog
              isRecurring
              url={share.recurringPaymentLinkUrl}
              country={share.country}
              currency={share.currency}
              amount={share.amount}
              description={share.description}
              onClose={() => setShare(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CreatedRecurringPaymentLink;
